import { EyeOpenIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import {
  IMissingBaseline,
  IMissingBaselineReason,
  IPatientWithBaselines,
} from "../../../@types/admin/AdminBaseline";
import { TableSorted } from "../../../components/TableSorted";
import {
  useMissingBaselines,
  usePatientsWithBaselines,
} from "../../../hooks/queries/admin/baselines";
import { useModal } from "../../../hooks/useModal";
import { humanizeEnumName } from "../../../utils/enumNames";
import {
  EmailLink,
  ErrorMessage,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageInfoTooltip,
  PageTitle,
  PageTitleGroup,
  RowAction,
  SearchInput,
  StatusBadge,
  Tab,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  TabsContainer,
  useMockPagination,
} from "../shared";
import { RowActions } from "./AdminPatients.styles";
import { PatientBaselinesModal } from "./PatientBaselinesModal";

type IBaselinesTab = "with" | "missing";

const WITH_SEARCH_FIELDS: (keyof IPatientWithBaselines)[] = [
  "patientId",
  "patientName",
  "patientEmail",
  "wardName",
];

const MISSING_SEARCH_FIELDS: (keyof IMissingBaseline)[] = [
  "patientId",
  "patientName",
  "patientEmail",
  "wardName",
  "workflowTitle",
  "measurementType",
];

/**
 * How the report explains itself. "IshltOnly" is the one Retool could not
 * report at all, and it is the one with a different fix: the patient HAS a
 * baseline, it just isn't one a workflow rule can use.
 */
const MISSING_REASON_LABELS: Record<IMissingBaselineReason, string> = {
  NoBaseline: "No baseline",
  IshltOnly: "ISHLT baseline only",
  ArchivedOnly: "Archived only",
};

/**
 * Baselines admin page (EN-5168), migrated from the Retool Baselines app
 * (`page_Baselines`; its `moduleBaselines` sibling is dead — see
 * `PatientBaselinesPanel`).
 *
 * Retool stacked two containers on one screen. They answer different
 * questions — "what baselines do we hold" and "whose monitoring is not
 * working" — so they are tabs here, and the expensive missing-baselines report
 * only runs once its tab is opened.
 *
 * The missing-baselines report is deliberately wider than the Retool query it
 * replaces, in three ways that all follow what the workflow scheduler actually
 * does: secondary-ward memberships, the two absolute baseline comparators, and
 * baselines that exist but are ISHLT-only. Each row carries the field that
 * explains why it is there. See `portal/retool-migration.md`.
 */
export function AdminBaselines() {
  document.title = "Baselines - Admin";

  const { openModal } = useModal();
  const [tab, setTab] = useState<IBaselinesTab>("with");
  const isWithTab = tab === "with";

  const {
    patients,
    isLoading: patientsLoading,
    isError: patientsError,
  } = usePatientsWithBaselines();
  const {
    missingBaselines,
    isLoading: missingLoading,
    isError: missingError,
  } = useMissingBaselines(!isWithTab);

  const withPagination = useMockPagination({
    data: patients,
    searchFields: WITH_SEARCH_FIELDS,
  });
  const missingPagination = useMockPagination({
    data: missingBaselines,
    searchFields: MISSING_SEARCH_FIELDS,
  });

  const withColumns: ColumnDef<IPatientWithBaselines, string>[] = useMemo(
    () => [
      {
        id: "patientId",
        header: "Patient ID",
        accessorFn: (patient) => String(patient.patientId),
        enableSorting: true,
      },
      { accessorKey: "patientName", header: "Name", enableSorting: true },
      {
        accessorKey: "patientEmail",
        header: "Email",
        cell: ({ row }) => (
          <EmailLink
            href={`mailto:${row.original.patientEmail}`}
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.patientEmail}
          </EmailLink>
        ),
        enableSorting: true,
      },
      {
        // cms_patient.hospital_id — one of the patient's wards, not the set
        // (EN-4960). Retool labelled it "Hospital Ward Name", which read as
        // *the* ward.
        accessorKey: "wardName",
        header: "Primary ward",
        enableSorting: true,
      },
      {
        id: "wardId",
        header: "Ward ID",
        accessorFn: (patient) => String(patient.wardId ?? ""),
        enableSorting: true,
      },
      {
        // Retool's query did not filter archived patients, so this column says
        // which rows are one rather than letting them pass as active.
        id: "archived",
        header: "Patient record",
        accessorFn: (patient) => (patient.archived ? "Archived" : "Active"),
        cell: ({ row }) => (
          <StatusBadge status={row.original.archived ? "inactive" : "active"}>
            {row.original.archived ? "Archived" : "Active"}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions>
            <RowAction
              label="View baselines"
              icon={<EyeOpenIcon />}
              onClick={() => {
                // Retool opened this on a double-click with no other
                // affordance; an explicit action makes the drill-down
                // discoverable (same call as EN-5156's Treatments page).
                openModal(<PatientBaselinesModal patient={row.original} />, {
                  width: "min(1100px, 96vw)",
                  maxHeight: "90vh",
                  showCloseButton: true,
                });
              }}
            />
          </RowActions>
        ),
      },
    ],
    []
  );

  const missingColumns: ColumnDef<IMissingBaseline, string>[] = useMemo(
    () => [
      {
        id: "patientId",
        header: "Patient ID",
        accessorFn: (row) => String(row.patientId),
        enableSorting: true,
      },
      { accessorKey: "patientName", header: "Name", enableSorting: true },
      {
        accessorKey: "patientEmail",
        header: "Email",
        cell: ({ row }) => (
          <EmailLink
            href={`mailto:${row.original.patientEmail}`}
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.patientEmail}
          </EmailLink>
        ),
        enableSorting: true,
      },
      { accessorKey: "wardName", header: "Ward", enableSorting: true },
      {
        // The ward the workflow reaches the patient through. Retool only ever
        // saw primary wards, so it never had to distinguish them.
        id: "isPrimaryWard",
        header: "Ward is",
        accessorFn: (row) => (row.isPrimaryWard ? "Primary" : "Secondary"),
        enableSorting: true,
      },
      {
        accessorKey: "workflowTitle",
        header: "Workflow",
        enableSorting: true,
      },
      {
        id: "measurementType",
        header: "Measurement",
        accessorFn: (row) => humanizeEnumName(row.measurementType),
        enableSorting: true,
      },
      {
        // Which baseline comparison the rule makes. Retool reported only the
        // two percentage ones; a LessThanBaseline rule was invisible.
        id: "comparatorType",
        header: "Rule compares",
        accessorFn: (row) => humanizeEnumName(row.comparatorType),
        enableSorting: true,
      },
      {
        id: "missingReason",
        header: "Why",
        accessorFn: (row) =>
          MISSING_REASON_LABELS[row.missingReason] ?? row.missingReason,
        cell: ({ row }) => (
          <StatusBadge
            status={
              row.original.missingReason === "IshltOnly" ? "warning" : "error"
            }
          >
            {MISSING_REASON_LABELS[row.original.missingReason] ??
              row.original.missingReason}
          </StatusBadge>
        ),
        enableSorting: true,
      },
    ],
    []
  );

  const pagination = isWithTab ? withPagination : missingPagination;

  return (
    <PageContainer>
      <PageHeader>
        <PageTitleGroup>
          <PageTitle>Baselines</PageTitle>
          {/* Follows the tab: the two lists answer different questions, and
              the "missing" one is the half that needs explaining. */}
          <PageInfoTooltip title="Baselines">
            {isWithTab
              ? "Patients with at least one recorded baseline. Open a row for the full baseline record, archived ones included."
              : "Patients whose ward runs a published workflow with a baseline rule, and who have no baseline that rule can use. Until the baseline is recorded, the workflow discards their readings and never fires."}
          </PageInfoTooltip>
        </PageTitleGroup>
      </PageHeader>

      <TabsContainer>
        <Tab active={isWithTab} onClick={() => setTab("with")}>
          With baselines
        </Tab>
        <Tab active={!isWithTab} onClick={() => setTab("missing")}>
          Missing baselines
        </Tab>
      </TabsContainer>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder={
            isWithTab
              ? "Search by patient ID, name, email, or ward..."
              : "Search by patient, ward, workflow, or measurement..."
          }
          value={pagination.searchTerm}
          onChange={(e) => pagination.setSearchTerm(e.target.value)}
        />
      </FiltersContainer>

      {isWithTab && patientsError && (
        <ErrorMessage>
          Failed to load the patients with baselines. Try refreshing the page.
        </ErrorMessage>
      )}
      {!isWithTab && missingError && (
        <ErrorMessage>
          Failed to load the missing-baselines report. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper
          refreshKeys={[
            ["admin-patients-with-baselines"],
            ["admin-missing-baselines"],
          ]}
        >
          {isWithTab ? (
            <TableSorted
              columns={withColumns}
              data={withPagination.items}
              emptyStateText={
                patientsLoading ? <TableLoadingState /> : "No rows found"
              }
            />
          ) : (
            <TableSorted
              columns={missingColumns}
              data={missingPagination.items}
              emptyStateText={
                missingLoading ? (
                  <TableLoadingState />
                ) : (
                  "No patient is missing a baseline a workflow needs"
                )
              }
            />
          )}
        </TableWrapper>
        {isWithTab ? (
          <TablePagination
            pagination={withPagination.pagination}
            onPageChange={withPagination.setPage}
            onPageSizeChange={withPagination.setPageSize}
            exportCsv={{
              filename: "patients-with-baselines",
              columns: withColumns,
              rows: withPagination.allItems,
            }}
          />
        ) : (
          <TablePagination
            pagination={missingPagination.pagination}
            onPageChange={missingPagination.setPage}
            onPageSizeChange={missingPagination.setPageSize}
            exportCsv={{
              filename: "missing-baselines",
              columns: missingColumns,
              rows: missingPagination.allItems,
            }}
          />
        )}
      </TableContainer>
    </PageContainer>
  );
}
